import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { usersApi } from '../lib/api';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Users as UsersIcon, Plus, Trash2, Shield, ShieldAlert, User, UserPlus, Edit2, X, Mail, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, cn } from '../lib/utils';

const roleIcons = { admin: ShieldAlert, manager: Shield, staff: User };
const roleColors = {
    admin: 'bg-rose-500/10 text-rose-400',
    manager: 'bg-amber-500/10 text-amber-400',
    staff: 'bg-sky-500/10 text-sky-400',
};

export default function UserManagement() {
    const { user: currentUser, isAdmin } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ name: '', email: '', password: '', role: 'staff' });
    const [creating, setCreating] = useState(false);

    // Edit state
    const [editingUser, setEditingUser] = useState(null);
    const [editForm, setEditForm] = useState({ name: '', email: '', role: '' });
    const [saving, setSaving] = useState(false);
    const [resettingId, setResettingId] = useState(null);

    const loadUsers = () => {
        setLoading(true);
        usersApi.getAll().then(({ data }) => setUsers(data)).catch(() => toast.error('Failed to load users')).finally(() => setLoading(false));
    };

    useEffect(() => { loadUsers(); }, []);

    const handleCreate = async () => {
        if (!form.name || !form.email || !form.password) { toast.error('All fields are required'); return; }
        setCreating(true);
        try {
            await usersApi.create({ name: form.name, email: form.email, password: form.password, role: form.role });
            toast.success('User created');
            setShowForm(false);
            setForm({ name: '', email: '', password: '', role: 'staff' });
            loadUsers();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create user');
        } finally { setCreating(false); }
    };

    const handleDelete = async (userId) => {
        if (!window.confirm('Delete this user?')) return;
        try { await usersApi.delete(userId); toast.success('User deleted'); loadUsers(); }
        catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete user'); }
    };

    const handleToggleStatus = async (userId, isActive) => {
        try { await usersApi.toggleStatus(userId, !isActive); loadUsers(); }
        catch { toast.error('Failed to update status'); }
    };

    const startEdit = (u) => {
        setEditingUser(u);
        setEditForm({ name: u.name, email: u.email, role: u.role });
    };

    const handleSaveEdit = async () => {
        if (!editForm.name.trim() || !editForm.email.trim()) { toast.error('Name and email are required'); return; }
        setSaving(true);
        try {
            // Update name/email if changed
            const updates = {};
            if (editForm.name !== editingUser.name) updates.name = editForm.name;
            if (editForm.email !== editingUser.email) updates.email = editForm.email;
            if (Object.keys(updates).length > 0) {
                await usersApi.update(editingUser.id, updates);
            }
            // Update role if changed
            if (editForm.role !== editingUser.role) {
                await usersApi.updateRole(editingUser.id, editForm.role);
            }
            if (Object.keys(updates).length > 0 || editForm.role !== editingUser.role) {
                toast.success('User updated');
            }
            setEditingUser(null);
            loadUsers();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to update');
        } finally { setSaving(false); }
    };

    const handleSendReset = async (userId, userName) => {
        setResettingId(userId);
        try {
            const { data } = await usersApi.sendPasswordReset(userId);
            toast.success(data.message);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to send reset email');
        } finally { setResettingId(null); }
    };

    return (
        <Layout>
            <div data-testid="user-management-page">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-100 flex items-center gap-2">
                            <UsersIcon className="h-6 w-6 brand-icon" /> Staff Management
                        </h1>
                        <p className="text-slate-400 text-sm mt-1">{users.length} team members</p>
                    </div>
                    <Button onClick={() => setShowForm(!showForm)} className="brand-btn font-medium" data-testid="add-user-button">
                        <UserPlus className="h-4 w-4 mr-1" /> Add Staff
                    </Button>
                </div>

                {/* Add user form */}
                {showForm && (
                    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5 mb-6" data-testid="add-user-form">
                        <h2 className="text-lg font-semibold text-slate-200 mb-4">New Staff Member</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label className="text-slate-300">Full Name *</Label>
                                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    className="mt-1 bg-slate-800 border-slate-700 text-slate-100" placeholder="Full name" data-testid="new-user-name" />
                            </div>
                            <div>
                                <Label className="text-slate-300">Email *</Label>
                                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    className="mt-1 bg-slate-800 border-slate-700 text-slate-100" placeholder="email@business.com" data-testid="new-user-email" />
                            </div>
                            <div>
                                <Label className="text-slate-300">Password *</Label>
                                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                                    className="mt-1 bg-slate-800 border-slate-700 text-slate-100" placeholder="Initial password" data-testid="new-user-password" />
                            </div>
                            {isAdmin && (
                                <div>
                                    <Label className="text-slate-300">Role</Label>
                                    <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                                        className="mt-1 w-full h-10 rounded-md border border-slate-700 bg-slate-800 px-3 text-sm text-slate-100" data-testid="new-user-role">
                                        <option value="staff">Staff</option>
                                        <option value="manager">Manager</option>
                                    </select>
                                </div>
                            )}
                        </div>
                        <div className="flex gap-2 mt-4">
                            <Button onClick={handleCreate} disabled={creating} className="brand-btn font-medium" data-testid="save-user-button">
                                {creating ? 'Creating...' : 'Create User'}
                            </Button>
                            <Button variant="outline" onClick={() => setShowForm(false)} className="border-slate-700 text-slate-300">Cancel</Button>
                        </div>
                    </div>
                )}

                {/* Edit user modal */}
                {editingUser && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
                        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setEditingUser(null)} />
                        <div className="relative bg-slate-900 border border-slate-800 rounded-xl p-5 w-full max-w-md" data-testid="edit-user-modal">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-semibold text-slate-200">Edit User</h2>
                                <Button variant="ghost" size="icon" onClick={() => setEditingUser(null)} className="text-slate-400 h-7 w-7">
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <Label className="text-slate-300">Name</Label>
                                    <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                        className="mt-1 bg-slate-800 border-slate-700 text-slate-100" data-testid="edit-user-name" />
                                </div>
                                <div>
                                    <Label className="text-slate-300">Email</Label>
                                    <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                                        className="mt-1 bg-slate-800 border-slate-700 text-slate-100" data-testid="edit-user-email" />
                                </div>
                                <div>
                                    <Label className="text-slate-300">Role</Label>
                                    <select value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                                        className="mt-1 w-full h-10 rounded-md border border-slate-700 bg-slate-800 px-3 text-sm text-slate-100" data-testid="edit-user-role">
                                        <option value="staff">Staff</option>
                                        <option value="manager">Manager</option>
                                        {isAdmin && <option value="admin">Admin</option>}
                                    </select>
                                </div>
                            </div>
                            <div className="flex gap-2 mt-5">
                                <Button onClick={handleSaveEdit} disabled={saving} className="brand-btn font-medium" data-testid="save-edit-user-button">
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </Button>
                                <Button variant="outline" onClick={() => setEditingUser(null)} className="border-slate-700 text-slate-300">Cancel</Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* User list */}
                {loading ? (
                    <div className="space-y-3">{[1, 2, 3].map(i => <div key={i} className="h-16 bg-slate-900/60 rounded-xl animate-pulse" />)}</div>
                ) : users.length === 0 ? (
                    <div className="text-center py-16 text-slate-500"><UsersIcon className="h-12 w-12 mx-auto mb-3 opacity-30" /><p>No staff members</p></div>
                ) : (
                    <div className="space-y-2">
                        {users.map((u) => {
                            const Icon = roleIcons[u.role] || User;
                            const isSelf = u.id === currentUser?.id;
                            return (
                                <div key={u.id} className={cn("bg-slate-900/60 border border-slate-800 rounded-xl p-4", !u.is_active && "opacity-50")}
                                    data-testid={`user-card-${u.id}`}>
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                            <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${roleColors[u.role] || roleColors.staff}`}>
                                                <Icon className="h-5 w-5" />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium text-slate-200 truncate">{u.name}{isSelf && ' (You)'}</p>
                                                <p className="text-xs text-slate-500 truncate">{u.email}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0 flex-wrap">
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${roleColors[u.role] || roleColors.staff}`}>{u.role}</span>
                                            <span className="text-[11px] text-slate-600">{formatDate(u.created_at)}</span>
                                            {!isSelf && (
                                                <>
                                                    <Button size="sm" variant="ghost" className="text-slate-400 hover:text-slate-200 hover:bg-slate-700 h-7 w-7 p-0"
                                                        onClick={() => startEdit(u)} data-testid={`edit-user-${u.id}`} title="Edit user">
                                                        <Edit2 className="h-3.5 w-3.5" />
                                                    </Button>
                                                    <Button size="sm" variant="ghost" className="text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 h-7 w-7 p-0"
                                                        onClick={() => handleSendReset(u.id, u.name)} disabled={resettingId === u.id}
                                                        data-testid={`reset-password-${u.id}`} title="Send password reset email">
                                                        {resettingId === u.id ? <Mail className="h-3.5 w-3.5 animate-pulse" /> : <KeyRound className="h-3.5 w-3.5" />}
                                                    </Button>
                                                    <Button size="sm" variant="ghost"
                                                        className={u.is_active ? "brand-text hover:bg-slate-700 text-xs h-7" : "text-emerald-400 hover:bg-emerald-500/10 text-xs h-7"}
                                                        onClick={() => handleToggleStatus(u.id, u.is_active)} data-testid={`toggle-status-${u.id}`}>
                                                        {u.is_active ? 'Disable' : 'Enable'}
                                                    </Button>
                                                    <Button size="sm" variant="ghost" className="text-rose-400 hover:bg-rose-500/10 h-7 w-7 p-0"
                                                        onClick={() => handleDelete(u.id)} data-testid={`delete-user-${u.id}`}>
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </Layout>
    );
}
